import toast from "react-hot-toast";
import { ProjectEnum } from "../../../../models/common/Enums";
import { CompanyPoService } from "../../../../services/CompanyPoService";
import { KTCard } from "../../../../_metronic/helpers/components/KTCard";
import { KTCardBody } from "../../../../_metronic/helpers/components/KTCardBody";
import CustomSingleSelect, { CustomSingleSelectModel } from "../../../../components/CustomSingleSelect";
import CustomTextInput from "../../../../components/CustomTextInput";
import { CompanyService } from "../../../../services/CompanyService";
import { ConstantService } from "../../../../services/ConstantService";
import { ServiceService } from "../../../../services/ServiceService";

interface Props {
    generalInformationRef: any
    refNo: string | undefined
    setRefNo: React.Dispatch<React.SetStateAction<string | undefined>>
    selectedCustomer: CustomSingleSelectModel | undefined
    setSelectedCustomer: React.Dispatch<React.SetStateAction<CustomSingleSelectModel | undefined>>
    shippingCode: string | undefined
    setShippingCode: React.Dispatch<React.SetStateAction<string | undefined>>
    integrationRefNo: string | undefined
    setIntegrationRefNo: React.Dispatch<React.SetStateAction<string | undefined>>
    selectedDivision: CustomSingleSelectModel | undefined
    setSelectedDivision: React.Dispatch<React.SetStateAction<CustomSingleSelectModel | undefined>>
    selectedPo: CustomSingleSelectModel | undefined
    setSelectedPo: React.Dispatch<React.SetStateAction<CustomSingleSelectModel | undefined>>
    poNo: string | undefined
    setPoNo: React.Dispatch<React.SetStateAction<string | undefined>>
    /** "Yurtiçi Kargo Sipariş Güncelleme" rolü var mı: yoksa bu dört alan salt okunur. */
    canUpdatePo: boolean
    /** Kayıt elle güncellenip kilitlendi mi (senkron ve toplu PO güncellemesi bu alanlara dokunmaz). */
    isPoManual: boolean
    /** PO tanımlarının geçerliliği bu tarihe göre bakılır (gönderi oluşturma tarihi). */
    shippingCreateDate: string | undefined
    poManualInfo: string | undefined
    savePo: () => void
    unlockPo: () => void
}

const GeneralInformation = ({
    generalInformationRef,
    refNo,
    setRefNo,
    selectedCustomer,
    setSelectedCustomer,
    setShippingCode,
    shippingCode,
    integrationRefNo,
    setIntegrationRefNo,
    selectedDivision,
    setSelectedDivision,
    selectedPo,
    setSelectedPo,
    poNo,
    setPoNo,
    canUpdatePo,
    isPoManual,
    shippingCreateDate,
    poManualInfo,
    savePo,
    unlockPo
}: Props) => {
    /** Müşteriye tanımlı ve tarihi geçerli bölümler; aynı bölüm birden çok tanımda geçerse bir kez listelenir. */
    const divisionOptions = async () => {
        const cevap: any = await CompanyPoService.getCompanyPoList(shippingCreateDate, selectedCustomer, undefined, ProjectEnum.YURTICI_KARGO, true);

        if (cevap?.isSuccess && Array.isArray(cevap.data)) {
            const gorulenler: string[] = [];

            cevap.data = cevap.data.filter((x: any) => {
                if (!x.divisionId || gorulenler.includes(x.divisionId))
                    return false;

                gorulenler.push(x.divisionId);
                return true;
            });
        }

        return cevap;
    }

    /**
     * Bölüm seçilince PO ve PO Numarası, o bölümün PO tanımından otomatik gelir; elle yazılmaz.
     * Tanım bulunamazsa iki alan da temizlenir ve kullanıcı bilgilendirilir.
     */
    const divisionChanged = async (value: CustomSingleSelectModel | undefined) => {
        setSelectedDivision(value);

        if (!canUpdatePo)
            return;

        setSelectedPo(undefined);
        setPoNo(undefined);

        if (!value?.value)
            return;

        try {
            const cevap: any = await CompanyPoService.getCompanyPoList(shippingCreateDate, selectedCustomer, value, ProjectEnum.YURTICI_KARGO, true);

            if (!cevap?.isSuccess) {
                toast.error(cevap?.message);
                return;
            }

            const tanim = (cevap.data ?? [])[0];

            if (!tanim) {
                toast.error('Seçilen bölüm için tanımlı PO bulunamadı.');
                return;
            }

            if (tanim.poId)
                setSelectedPo({ label: tanim.poName, value: tanim.poId });

            setPoNo(tanim.poNo ?? undefined);
        } catch (error: any) {
            toast.error(error);
        }
    }

    return (
        <div className='containerPart' ref={generalInformationRef} >
            <KTCard className="ebbe-card mt-10">
                <KTCardBody>
                    <div className="d-flex align-items-center justify-content-between mb-10">
                        <h4 className="m-0 text-black">Genel Bilgiler</h4>

                        <div className="d-flex align-items-center gap-3">
                            {
                                isPoManual && (
                                    <span className="badge badge-light-warning" title="Entegrasyon ve toplu PO güncellemesi bu kaydın Müşteri / Bölüm / PO / PO Numarası alanlarını değiştirmez.">
                                        Müşteri / Bölüm / PO elle güncellendi{poManualInfo ? ' · ' + poManualInfo : ''}
                                    </span>
                                )
                            }

                            {
                                canUpdatePo && (
                                    <button type="button" className="btn btn-sm btn-light-primary" onClick={savePo}>
                                        Müşteri / Bölüm / PO Bilgisini Kaydet
                                    </button>
                                )
                            }

                            {
                                canUpdatePo && isPoManual && (
                                    <button type="button" className="btn btn-sm btn-light-danger" onClick={unlockPo}>
                                        Kilidi Kaldır
                                    </button>
                                )
                            }
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-4">
                            <CustomTextInput labelText='Ref No' isLabelRequired value={refNo} setValue={setRefNo} isDisabled />
                        </div>

                        <div className="col-md-4">
                            <CustomSingleSelect
                                labelText='Müşteri'
                                isLabelRequired
                                selectedValue={selectedCustomer}
                                onChange={setSelectedCustomer}
                                onMenuOpen={[
                                    () => CompanyService.getCompanyList({ isCustomer: true })
                                ]}
                                labelName='name'
                                valueName='companyId'
                                disabled={!canUpdatePo}
                            />
                        </div>

                        <div className="col-md-4">
                            <CustomTextInput labelText='Gönderi Kodu' isLabelRequired value={shippingCode} setValue={setShippingCode} isDisabled />
                        </div>
                    </div>

                    <div className="row mt-5">
                        <div className="col-md-4">
                            <CustomTextInput labelText='Entegrasyon Ref No' isLabelRequired value={integrationRefNo} setValue={setIntegrationRefNo} isDisabled />
                        </div>

                        <div className="col-md-4">
                            <CustomSingleSelect
                                labelText="Bölüm"
                                isLabelRequired
                                selectedValue={selectedDivision}
                                onChange={divisionChanged}
                                onMenuOpen={selectedCustomer ? divisionOptions : undefined}
                                labelName='divisionName'
                                valueName="divisionId"
                                disabled={!canUpdatePo || !selectedCustomer}
                            />
                        </div>

                        <div className="col-md-4">
                            <CustomSingleSelect
                                labelText="PO"
                                isLabelRequired
                                selectedValue={selectedPo}
                                onChange={setSelectedPo}
                                onMenuOpen={() => ServiceService.getServiceList(true)}
                                labelName='name'
                                valueName='serviceId'
                                additionalValue1Name='incomePercentageName'
                                disabled={!canUpdatePo || !selectedDivision}
                            />
                        </div>
                    </div>

                    <div className="row mt-5">
                        <div className="col-md-4">
                            {/* PO ve PO Numarası elle yazılmaz; bölüm seçilince PO tanımından gelir. */}
                            <CustomTextInput labelText="PO Numarası" isLabelRequired value={poNo} setValue={setPoNo} isDisabled />
                        </div>
                    </div>
                </KTCardBody>
            </KTCard>
        </div>
    )
}

export default GeneralInformation;